import {
  animate,
  state,
  style,
  transition,
  trigger,
  type AnimationMetadata,
  type AnimationTriggerMetadata,
} from '@angular/animations';

export function appearFromTopStyle(
  amount: string,
  opacity: boolean,
): Record<string, string | number> {
  return {
    transform: `translateY(-${amount})`,
    zIndex: 1000,
    display: 'initial',
    opacity: opacity ? 0 : 1,
  };
}

export function appearFromRightStyle(
  amount: string,
  opacity: boolean,
): Record<string, string | number> {
  return {
    transform: `translateX(${amount})`,
    zIndex: 1000,
    display: 'initial',
    opacity: opacity ? 0 : 1,
  };
}

export function appearFromBottomStyle(
  amount: string,
  opacity: boolean,
): Record<string, string | number> {
  return {
    transform: `translateY(${amount})`,
    zIndex: 1000,
    display: 'initial',
    opacity: opacity ? 0 : 1,
  };
}

export function appearFromLeftStyle(
  amount: string,
  opacity: boolean,
): Record<string, string | number> {
  return {
    transform: `translateX(-${amount})`,
    zIndex: 1000,
    display: 'initial',
    opacity: opacity ? 0 : 1,
  };
}

export function appearToTopBottomStyle(): Record<string, string | number> {
  return {
    transform: 'translateY(0)',
    zIndex: 1000,
    opacity: 1,
  };
}

export function appearToRightLeftStyle(): Record<string, string | number> {
  return {
    transform: 'translateX(0)',
    zIndex: 1000,
    opacity: 1,
  };
}

export function easeInTopSteps(
  timer: number,
  amount: string,
  opacity: boolean,
): AnimationMetadata[] {
  return [
    style(appearFromTopStyle(amount, opacity)),
    animate(timer + 'ms ease-in', style(appearToTopBottomStyle())),
  ];
}

export function easeInRightSteps(
  timer: number,
  amount: string,
  opacity: boolean,
): AnimationMetadata[] {
  return [
    style(appearFromRightStyle(amount, opacity)),
    animate(timer + 'ms ease-in', style(appearToRightLeftStyle())),
  ];
}

export function easeInBottomSteps(
  timer: number,
  amount: string,
  opacity: boolean,
): AnimationMetadata[] {
  return [
    style(appearFromBottomStyle(amount, opacity)),
    animate(timer + 'ms ease-in', style(appearToTopBottomStyle())),
  ];
}

export function easeInLeftSteps(
  timer: number,
  amount: string,
  opacity: boolean,
): AnimationMetadata[] {
  return [
    style(appearFromLeftStyle(amount, opacity)),
    animate(timer + 'ms ease-in', style(appearToRightLeftStyle())),
  ];
}

export function easeOutTopSteps(
  timer: number,
  amount: string,
  opacity: boolean,
): AnimationMetadata[] {
  return [
    style(appearToTopBottomStyle()),
    animate(timer + 'ms ease-out', style(appearFromTopStyle(amount, opacity))),
  ];
}

export function easeOutRightSteps(
  timer: number,
  amount: string,
  opacity: boolean,
): AnimationMetadata[] {
  return [
    style(appearToRightLeftStyle()),
    animate(
      timer + 'ms ease-out',
      style(appearFromRightStyle(amount, opacity)),
    ),
  ];
}

export function easeOutBottomSteps(
  timer: number,
  amount: string,
  opacity: boolean,
): AnimationMetadata[] {
  return [
    style(appearToTopBottomStyle()),
    animate(
      timer + 'ms ease-out',
      style(appearFromBottomStyle(amount, opacity)),
    ),
  ];
}

export function easeOutLeftSteps(
  timer: number,
  amount: string,
  opacity: boolean,
): AnimationMetadata[] {
  return [
    style(appearToRightLeftStyle()),
    animate(timer + 'ms ease-out', style(appearFromLeftStyle(amount, opacity))),
  ];
}

export function appearFromTop(
  timer: number = 300,
  amount: string = '100px',
  opacity: boolean = true,
): AnimationTriggerMetadata {
  return trigger('appearFromTop', [
    state('hide', style({ display: 'none' })),
    transition(':enter', easeInTopSteps(timer, amount, opacity)),
    transition(':leave', easeOutTopSteps(timer, amount, opacity)),
    transition('* => show', easeInTopSteps(timer, amount, opacity)),
    transition('* => hide', easeOutTopSteps(timer, amount, opacity)),
  ]);
}

export function appearFromRight(
  timer: number = 300,
  amount: string = '100px',
  opacity: boolean = true,
): AnimationTriggerMetadata {
  return trigger('appearFromRight', [
    state('hide', style({ display: 'none' })),
    transition(':enter', easeInRightSteps(timer, amount, opacity)),
    transition(':leave', easeOutRightSteps(timer, amount, opacity)),
    transition('* => show', easeInRightSteps(timer, amount, opacity)),
    transition('* => hide', easeOutRightSteps(timer, amount, opacity)),
  ]);
}

export function appearFromBottom(
  timer: number = 300,
  amount: string = '100px',
  opacity: boolean = true,
): AnimationTriggerMetadata {
  return trigger('appearFromBottom', [
    state('hide', style({ display: 'none' })),
    transition(':enter', easeInBottomSteps(timer, amount, opacity)),
    transition(':leave', easeOutBottomSteps(timer, amount, opacity)),
    transition('* => show', easeInBottomSteps(timer, amount, opacity)),
    transition('* => hide', easeOutBottomSteps(timer, amount, opacity)),
  ]);
}

export function appearFromLeft(
  timer: number = 300,
  amount: string = '100px',
  opacity: boolean = true,
): AnimationTriggerMetadata {
  return trigger('appearFromLeft', [
    state('hide', style({ display: 'none' })),
    transition(':enter', easeInLeftSteps(timer, amount, opacity)),
    transition(':leave', easeOutLeftSteps(timer, amount, opacity)),
    transition('* => show', easeInLeftSteps(timer, amount, opacity)),
    transition('* => hide', easeOutLeftSteps(timer, amount, opacity)),
  ]);
}

export function appearFrom(
  timer: number = 300,
  amount: string = '100px',
  opacity: boolean = true,
): AnimationTriggerMetadata {
  return trigger('appearFrom', [
    state('hide', style({ display: 'none' })),

    transition('void => top', easeInTopSteps(timer, amount, opacity)),
    transition('top => void', easeOutTopSteps(timer, amount, opacity)),

    transition('void => right', easeInRightSteps(timer, amount, opacity)),
    transition('right => void', easeOutRightSteps(timer, amount, opacity)),

    transition('void => bottom', easeInBottomSteps(timer, amount, opacity)),
    transition('bottom => void', easeOutBottomSteps(timer, amount, opacity)),

    transition('void => left', easeInLeftSteps(timer, amount, opacity)),
    transition('left => void', easeOutLeftSteps(timer, amount, opacity)),
  ]);
}
